import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Select, Button } from 'antd';
import {
  DeleteOutlined,
  PlusSquareOutlined
} from '@ant-design/icons';
import SessionForm from '../../../components/forms/SessionForm';
import SocialMediaModel from '../models/SocialMediaModel';
import { observer } from 'mobx-react-lite';
import HelpBoxCompanionModel from '../../../models/HelpBoxCompanionModel';
import { useTranslation } from 'react-i18next';
import Devider from '../../../components/Devider';
import AntdCustomButton from '../../../components/buttons/AntdCustomButton';
import { motion } from 'framer-motion';
import { fadeInUpVariants } from '../../../components/animations/StepAnimations';

interface FCProps { }

interface AdditionalPlatform {
  id: string;
  platform: string;
  url: string;
}

const SocialMediaForm: React.FC<FCProps> = observer(({ }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [defaultPlatforms, setDefaultPlatforms] = useState(['facebook', 'instagram', 'linkedin', 'x']);

  //used to track if default platforms have been modified by scraped social medias
  //when platforms are scraped, ones that are returned should be considered "default" regardless of their presence in the default list
  const [modifiedDefault, setModifiedDefault] = useState(false);

  const allPlatforms = [
    { key: 'facebook', label: 'Facebook' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'x', label: 'X (Former Twitter)' },
    { key: 'pinterest', label: 'Pinterest' },
    { key: 'tiktok', label: 'TikTok' },
    { key: 'youtube', label: 'YouTube' },
    { key: 'xing', label: 'Xing' }
  ];

  const [additionalPlatforms, setAdditionalPlatforms] = useState<AdditionalPlatform[]>([]);

  // Disable help box on component mount
  useEffect(() => {
    HelpBoxCompanionModel.setDisplayHelpBox(false);
  }, []);

  // Initialize form values from SocialMediaModel
  const initialValues = useMemo(() => {
    const values: Record<string, string> = {};
    if (
      !SocialMediaModel.isLoadingSocialProfiles &&
      SocialMediaModel.socialMediaProfiles.size > 0
    ) {
      const formFields = [
        'facebook',
        'instagram',
        'linkedin',
        'pinterest',
        'tiktok',
        'x',
        'xing',
        'youtube'
      ];

      for (const fieldName of formFields) {
        if (SocialMediaModel.socialMediaProfiles.has(fieldName)
          && SocialMediaModel.socialMediaProfiles.get(fieldName)?.url) {
          values[fieldName] = SocialMediaModel.socialMediaProfiles.get(fieldName)?.url;
        }
      }
    }
    return values;
  }, [SocialMediaModel.isLoadingSocialProfiles, SocialMediaModel.socialMediaProfiles]);
  // Add a ref to track if initial load has happened
  const initialLoadDone = React.useRef(false);

  // Load saved data and set up additional platforms
  useEffect(() => {
    // Only process scraped data on first load
    if (!initialLoadDone.current) {
      const savedPlatforms = Array.from(SocialMediaModel.socialMediaProfiles.entries());
      const formValues: Record<string, string> = {};

      if (!modifiedDefault) {
        savedPlatforms.forEach(([key, value]) => {
          formValues[key] = value.url;
          // Add non-default platforms to defaultPlatforms
          if (!defaultPlatforms.includes(key)) {
            setDefaultPlatforms(prev => [...prev, key]);
          }
        });
        setModifiedDefault(true);
      }

      // Merge with initial values
      const mergedValues = { ...initialValues, ...formValues };
      form.setFieldsValue(mergedValues);

      initialLoadDone.current = true;
    } else if (additionalPlatforms.length > 0) {
      //  Оnly update additionalPlatforms data, don't reset everything
      const formUpdate: Record<string, string> = {};

      additionalPlatforms.forEach(platform => {
        if (platform.platform && platform.url) {
          formUpdate[platform.platform] = platform.url;
        }
      });

      // Only update the specific fields related to additionalPlatforms
      if (Object.keys(formUpdate).length > 0) {
        form.setFieldsValue(formUpdate);
      }
    }
  }, [initialValues, form, additionalPlatforms]);

  const saveAllData = () => {
    const formValues = form.getFieldsValue();

    // Save default platforms
    defaultPlatforms.forEach(platform => {
      const url = formValues[platform];
      if (url) {
        SocialMediaModel.socialMediaProfiles.set(platform, { url });
      } else {
        SocialMediaModel.socialMediaProfiles.delete(platform);
      }
    });

    // Save additional platforms
    additionalPlatforms.forEach(platform => {
      let url = platform.url
      if (platform.platform && platform.url) {
        SocialMediaModel.socialMediaProfiles.set(platform.platform, { url });
      }
    });
  };

  const handleFormChange = () => {
    saveAllData();
  };

  const addMoreLinks = () => {
    const newPlatform = {
      id: `platform_${Date.now()}`,
      platform: '',
      url: ''
    };

    setAdditionalPlatforms(prev => [...prev, newPlatform]);
  };

  const updateAdditionalPlatform = (id: string, field: 'platform' | 'url', value: string) => {
    const platformIndex = additionalPlatforms.findIndex(p => p.id === id);
    if (platformIndex === -1) return;

    const platform = additionalPlatforms[platformIndex];
    const oldPlatform = platform.platform;

    // Create a new array with the updated platform
    const updated = [...additionalPlatforms];

    if (field === 'platform') {
      // Platform is changing - normalize to lowercase (matches how they're stored)
      const normalizedValue = value.toLowerCase();

      // Get the current URL for this platform
      let currentUrl = '';
      if (oldPlatform) {
        // Get URL from form
        currentUrl = form.getFieldValue(oldPlatform) || platform.url;

        // Remove old platform data from model
        if (oldPlatform !== normalizedValue) {
          SocialMediaModel.socialMediaProfiles.delete(oldPlatform);
        }
      }

      // Update platform in state
      updated[platformIndex] = {
        ...updated[platformIndex],
        platform: normalizedValue,
        url: currentUrl
      };

      // If we have URL data, update the form and model
      if (currentUrl && normalizedValue) {
        form.setFieldValue(normalizedValue, currentUrl);
        SocialMediaModel.socialMediaProfiles.set(normalizedValue, { url: currentUrl });
      }
    } else if (field === 'url') {
      // URL is changing
      updated[platformIndex] = {
        ...updated[platformIndex],
        url: value
      };

      // If platform exists, update the form and model
      if (platform.platform) {
        form.setFieldValue(platform.platform, value);
        SocialMediaModel.socialMediaProfiles.set(platform.platform, { url: value });
      }
    }

    setAdditionalPlatforms(updated);
  };

  const removePlatform = (id: string) => {
    const platformToRemove = additionalPlatforms.find(p => p.id === id);
    if (platformToRemove?.platform) {
      SocialMediaModel.socialMediaProfiles.delete(platformToRemove.platform);
    }

    setAdditionalPlatforms(additionalPlatforms.filter(p => p.id !== id));
  };

  const getAvailablePlatforms = () => {
    const usedPlatformKeys = new Set([
      ...defaultPlatforms,
      ...additionalPlatforms.map(p => p.platform).filter(Boolean)
    ]);

    return allPlatforms.filter(platform => !usedPlatformKeys.has(platform.key));
  };

  return (
    <SessionForm
      formName="socialMediaForm"
      form={form}
      formProps={{
        layout: 'vertical',
        onValuesChange: handleFormChange
      }}>
      <motion.div
        variants={fadeInUpVariants}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 font-bold"
      >
        {defaultPlatforms.map((key) => {
          const platform = allPlatforms.find((p) => p.key === key);
          return platform ? (
            <Form.Item
              key={key}
              name={key}
              label={platform.label}
            >
              <Input
                placeholder="https://www.domain.tld/"
                className="w-full"
              />
            </Form.Item>
          ) : null;
        })}
      </motion.div>

      {additionalPlatforms.length > 0 && (
        <React.Fragment>
          <Devider />
          <div className="space-y-4 mb-6 social-media-item company-data-form company-data-social-form">
            {additionalPlatforms.map((platform) => (
              <motion.div variants={fadeInUpVariants}>
                <div
                  key={platform.id}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div >
                    <Form.Item
                      name={`${platform.id}_platform`}
                      label={t('Social media platform name')}
                      rules={[
                        { required: true, message: t('Please select a platform') }
                      ]}>
                      <Select
                        placeholder={t('Select')}
                        labelInValue={true}
                        value={
                          platform.platform
                            ? {
                              value: platform.platform,
                              label:
                                allPlatforms.find((p) => p.key === platform.platform)?.label || platform.platform
                            }
                            : undefined
                        }
                        onChange={(option) =>
                          updateAdditionalPlatform(platform.id, 'platform', option.value)
                        }
                        className="w-full"
                        options={getAvailablePlatforms().map((p) => ({
                          label: p.label,
                          value: p.key
                        }))}
                      />
                    </Form.Item>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Form.Item
                        className="block text-sm font-medium text-gray-700 mb-1"
                        name={platform.platform}
                        label={t('Profile URL')}
                        rules={[
                          { required: true, message: t('Please enter a URL') }
                        ]}>
                        <Input
                          placeholder="https://www.domain.tld/"
                          className="w-full"
                          value={platform.url}
                          onChange={(e) =>
                            updateAdditionalPlatform(
                              platform.id,
                              'url',
                              e.target.value
                            )
                          }
                        />
                      </Form.Item>
                    </div>
                    <Button
                      type="text"
                      danger
                      style={{ color: '#DB3635' }}
                      icon={<DeleteOutlined />}
                      onClick={() => removePlatform(platform.id)}
                      className="mb-0 mt-5 md:mt-0 flex items-center justify-center self-center delete-button"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </React.Fragment>
      )}

      {(additionalPlatforms.length + defaultPlatforms.length) < allPlatforms.length && (
        <motion.div variants={fadeInUpVariants} className="mb-6 flex sm:justify-center">
          <AntdCustomButton
            appearance="important"
            type="primary"
            ghost
            icon={<PlusSquareOutlined />}
            onClick={addMoreLinks}
            className="flex items-center">
            {t('Add more links')}
          </AntdCustomButton>
        </motion.div>
      )}
    </SessionForm>
  );
});

export default SocialMediaForm;
